import { ReactElement, useCallback } from "react";

import {
  ActionKitIntegration,
  CampaignContactActionKitImportArgs,
  ImportCampaignContactsFromActionKitMutation,
  ImportCampaignContactsFromActionKitMutationVariables,
} from "~frontend/__generated__/graphql";
import { ACTION_KIT_CAMPAIGN_CONTACT_IMPORT } from "~frontend/graphql/campaigns";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../types";
import ImportForm from "./ImportForm";

export default function ActionKitImport({
  campaign,
  availableActionKitIntegrations,
  readOnly,
  previousJobArgs,
  displayErrorToast,
  resetContactSourceSettings,
}: {
  campaign: CampaignForEditor;
  availableActionKitIntegrations: ActionKitIntegration[];
  readOnly?: boolean;
  previousJobArgs?: CampaignContactActionKitImportArgs;
  displayErrorToast: (message: string, errors: unknown) => void;
  resetContactSourceSettings: (() => void) | null;
}): ReactElement {
  const ngpVanCampaignIntegrationEnabled = !!(
    campaign.integration && campaign.integration.service == "NGPVAN"
  );

  const [startImport] = useMutation<
    ImportCampaignContactsFromActionKitMutation,
    ImportCampaignContactsFromActionKitMutationVariables
  >(ACTION_KIT_CAMPAIGN_CONTACT_IMPORT);

  const startActionKitImport = useCallback(
    async ({
      integrationId,
      reportShortName,
      phoneNumberColumn,
      vanIdColumn,
    }: {
      integrationId: string;
      reportShortName: string;
      phoneNumberColumn: string;
      vanIdColumn?: string;
    }) => {
      const { errors } = await startImport({
        variables: {
          input: {
            campaignId: campaign.id,
            integrationId,
            reportShortName,
            phoneNumberColumn,
            vanIdColumn: vanIdColumn ?? null,
          },
        },
      });

      if (errors) {
        displayErrorToast("Failed to start ActionKit import", errors);
      }
    },
    [campaign, startImport, displayErrorToast]
  );

  return (
    <ImportForm
      onSubmit={readOnly ? undefined : startActionKitImport}
      availableActionKitIntegrations={availableActionKitIntegrations}
      initialValues={{
        campaignId: campaign.id,
        integrationId:
          previousJobArgs?.integrationId ??
          availableActionKitIntegrations[0]?.id,
        reportShortName: previousJobArgs?.reportShortName ?? "",
        phoneNumberColumn: previousJobArgs?.phoneNumberColumn ?? "cell",
        vanIdColumn: previousJobArgs?.vanIdColumn ?? "",
      }}
      readOnly={readOnly}
      resetContactSourceSettings={resetContactSourceSettings}
      ngpVanCampaignIntegrationEnabled={ngpVanCampaignIntegrationEnabled}
    />
  );
}
